import {
  CaseProgressBySectionStatNode,
  OverallProgressBySectionStatNode,
  GetPendingCasesStatsResult,
  CompletedCasesDatedStats,
  ReviewerCaseProgressBySectionStatNode,
  CompletedCasesAndSectionStats,
} from "generated/graphql";
import { SBRAdminStats, SBRReviewerStats } from "redux/slices/clientLens";

interface UseLensStats {
  overallCaseProgressStats?: OverallProgressBySectionStatNode;
  caseProgressByReviewerStats: GetPendingCasesStatsResult;
  caseProgressByReviewerTotal: number;
  caseProgressBySectionStats: CaseProgressBySectionStatNode[];
  completedCaseStats: CompletedCasesDatedStats[];
  completedCaseStatsTotal: number;
  completedSections: number;
  completedSectionsAndCases: number;
  caseProgressBySectionStatsTotal: number;
  reviewerCaseProgressBySection: ReviewerCaseProgressBySectionStatNode[];
  reviewerCompletedCaseStats?: CompletedCasesAndSectionStats;
  reviewerCaseProgressBySectionStatsTotal: number;
  reviewerCompletedSectionsAndCases: number;
  overallCompletedCases: number;
  overallValidationCases: number;
  overallReviewCases: number;
}

export function useLensStats({
  adminStats,
  reviewerStats,
}: {
  adminStats?: SBRAdminStats;
  reviewerStats?: SBRReviewerStats;
}): UseLensStats {
  const completedSections =
    adminStats?.caseProgressBySectionStats.reduce((count, stat) => count + stat.completeCount, 0) ?? 0;
  const completedSectionsAndCases =
    completedSections + (adminStats?.overallCaseProgressStats?.completedCasesCount ?? 0);
  const reviewerCompletedSectionsAndCases =
    (reviewerStats?.reviewerCompletedCaseStats?.completedSections ?? 0) +
    (reviewerStats?.reviewerCompletedCaseStats?.completedCases ?? 0);

  const caseProgressBySectionStatsTotal =
    adminStats?.caseProgressBySectionStats.reduce(
      (count, stat) => count + (stat.completeCount ?? 0) + (stat.reviewCount ?? 0) + (stat.validationCount ?? 0),
      0
    ) ?? 0;

  const reviewerCaseProgressBySectionStatsTotal =
    reviewerStats?.reviewerCaseProgressBySection.reduce(
      (count, stat) => count + (stat.completeCount ?? 0) + (stat.reviewCount ?? 0) + (stat.validationCount ?? 0),
      0
    ) ?? 0;
  const caseProgressByReviewerStats = adminStats?.caseProgressByReviewerStats ?? {
    byAssignee: [],
    unassignedCount: 0,
  };
  const caseProgressByReviewerTotal =
    caseProgressByReviewerStats.byAssignee.reduce((count, curr) => count + curr.count, 0) +
    caseProgressByReviewerStats.unassignedCount;
  const completedCaseStats = adminStats?.completedCaseStats ?? [];
  const completedCaseStatsTotal = completedCaseStats[completedCaseStats.length - 1]?.accumulatedCount ?? 0;

  return {
    overallCaseProgressStats: adminStats?.overallCaseProgressStats,
    overallCompletedCases: adminStats?.overallCaseProgressStats.completedCasesCount ?? 0,
    overallValidationCases: adminStats?.overallCaseProgressStats.validationCasesCount ?? 0,
    overallReviewCases: adminStats?.overallCaseProgressStats.reviewCasesCount ?? 0,
    reviewerCaseProgressBySection: reviewerStats?.reviewerCaseProgressBySection ?? [],
    reviewerCompletedCaseStats: reviewerStats?.reviewerCompletedCaseStats,
    caseProgressByReviewerStats,
    caseProgressByReviewerTotal,
    caseProgressBySectionStats: adminStats?.caseProgressBySectionStats ?? [],
    completedCaseStats,
    completedCaseStatsTotal,
    completedSections,
    completedSectionsAndCases,
    caseProgressBySectionStatsTotal,
    reviewerCaseProgressBySectionStatsTotal,
    reviewerCompletedSectionsAndCases,
  };
}
